import * as React from "react";
import { DataGrid, Popup } from "devextreme-react";
import { useCallback, useEffect, useMemo, useState } from "react";
//import dxFileManager, { SelectionChangedEvent, ToolbarItemClickEvent } from "devextreme/ui/file_manager";
import { dxButtonOptions } from "devextreme/ui/button";

import useDataSource from "./hooks/DataSource";
import { IDocument, IDocumentPrivate } from "../models/IDocument";

import { Column, Paging, Selection } from "devextreme-react/data-grid";
import { ToolbarItem } from "devextreme-react/popup";
import { SelectionChangedEvent } from "devextreme/ui/data_grid";
import { calcSize } from "./utils/FileUtils";

interface IProps<TModel> {
    selectedDocs?: TModel[] | TModel;
    saveSelection: (docs: TModel[]) => void;
    apiUrl: string;
    multiple?: boolean;
    showFileManager?: boolean;
    setShowFileManager: (newValue: boolean) => void
}

const FileSelection = <TModel extends { id?: number }>({ apiUrl, selectedDocs, saveSelection, multiple, showFileManager, setShowFileManager }: IProps<TModel>) => {
    
    const dsDocs = useDataSource(apiUrl);

    const initialSelection = useMemo(() => (
        (Array.isArray(selectedDocs) && selectedDocs)
        || (selectedDocs && [selectedDocs!])
        || []
    ), [selectedDocs])

    const [selection, setSelection] = useState(initialSelection)
    const [focusedRowKey, setFocusedRowKey] = useState(initialSelection?.[0]?.id)

    const selectionIds = useMemo(() => selection.map(doc => doc.id || 0), [selection])

    //set component state when props from parent changes
    useEffect(() => {
        setSelection(initialSelection)
        setFocusedRowKey(initialSelection?.[0]?.id)
    }, [initialSelection])

    //reset focused state when (re)openend
    useEffect(() => {
        showFileManager && setSelection(initialSelection)
        showFileManager && setFocusedRowKey(initialSelection?.[0]?.id)
    }, [initialSelection, showFileManager])


    const selectClickHandler = useCallback(() => {
        saveSelection(selection)
    }, [saveSelection, selection]);
    
    const rowDblClickHandler = useCallback(() => {
        (!multiple || selection.length === 1) && saveSelection(selection)
    }, [saveSelection, selection, multiple]);

    const closeFileManager = useCallback(() => setShowFileManager(false), [setShowFileManager])

    const selectBtnOptions = useMemo((): dxButtonOptions => ({
        text: "Selectie gebruiken (" + selection.length + ")",
        disabled: !selection.length,
        type: "default",
        icon: "check",
        onClick: selectClickHandler,
    }), [selectClickHandler, selection])


    const cancelBtnOptions = useMemo((): dxButtonOptions => ({
        text: "Annuleren",
        icon: "remove",
        onClick: closeFileManager,
    }), [closeFileManager])

    const selectionChangedHandler = useCallback((e: SelectionChangedEvent<TModel, number>) => {
        setSelection(e.selectedRowsData)
    }, [])

    const iconCellRenderHandler = useCallback(() => {
        return <i className="dx-icon dx-icon-pdffile" />
    }, [])

    const sizeCellValueHandler = useCallback((e: IDocument) => {
        return calcSize(e.size)
    }, [])


    const renderFileNameColomn = useCallback((data: { data: IDocumentPrivate, value: any, text: any }) => {
        return <a target="_blank" rel="noreferrer" href={apiUrl + "/" + data.data.id}>{data.value || "< Geen gegevens beschikbaar! >"}</a>;
    }, [apiUrl])

    return <Popup visible={showFileManager} closeOnOutsideClick onHiding={closeFileManager} deferRendering showCloseButton={false} >

        <ToolbarItem
            text={"Selecteer document" + (multiple ? "en" : "")}
            location="before" />
        
        <ToolbarItem
            widget="dxButton"
            location="after"
            options={selectBtnOptions} />

        <ToolbarItem
            widget="dxButton"
            location="after"
            options={cancelBtnOptions} />
        
        <DataGrid<TModel, number>
            dataSource={dsDocs}
            selectedRowKeys={selectionIds}
            //onSelectedRowKeysChange={setSelection}
            onSelectionChanged={selectionChangedHandler}
            focusedRowEnabled
            focusedRowKey={focusedRowKey}
            onFocusedRowKeyChange={setFocusedRowKey}
            height="100%"
            
            onRowDblClick={rowDblClickHandler}
        >
            <Paging
                enabled={false}
                //defaultPageSize={50}
            />
            <Column fixed width="auto" cellRender={iconCellRenderHandler} />
            <Column dataField="fileName" caption="Bestandsnaam" cellRender={renderFileNameColomn} />

            <Column dataField="countUsed" width="auto" caption="# Gebruikt" />
            <Column width={100} dataField="size" calculateCellValue={sizeCellValueHandler} alignment="right" calculateSortValue="size" />
            <Selection
                mode={multiple ? "multiple" : "single"}
                //showCheckBoxesMode={multiple ? "always" : "none"}
                showCheckBoxesMode="always"
            />
        </DataGrid>
    </Popup>
}
export default FileSelection;